import debounce from "lodash/debounce";
import { viewCartDataLayer } from "@/scripts/datalayer";

import DynamicSectionElement from "@/scripts/dynamic-section-element.js";
import { trapFocus } from "@shopify/theme-a11y";

class Cart extends DynamicSectionElement {
  constructor() {
    super();

    // Bind update method so it can be passed as callback argument
    this.onCartUpdate = this.onCartUpdate.bind(this);

    this.handleQuantityChange = debounce(
      () => {
        this.updateFromForm(this.querySelector("form"));
      },
      500,
      { leading: false, trailing: true }
    );

    // Use debounced event handler to update cart quanties
    this.addEventListener("change-quantity", () => {
      this.setAttribute("data-loading", true);
      this.handleQuantityChange();
    });

    if (document.body.classList.contains("template-cart")) {
      viewCartDataLayer();
    }

    this.addEventListener("keyup", (event) => {
      const eventCode = event.code.toUpperCase();
      if (eventCode === "ESCAPE") {
        this.closeDrawer();
      }
    });

    this.checkoutBtn = this.querySelector("#CartDrawer-Checkout");
    if (this.checkoutBtn) {
      this.checkoutBtn.addEventListener(
        "click",
        this.handleCheckout.bind(this)
      );
    }

    this.activeEvents = [];
    this.fetchActiveEvents();
  }

  getCustomerEventIds() {
    // Customers Event Tags
    const customerEventIds = [];
    const customerTags = window?.BARREL?.customerTags || [];
    if (customerTags.length) {
      customerTags.forEach((t) => {
        if (t.startsWith("Event:")) {
          const id = parseInt(t.replace("Event:", ""), 10);
          customerEventIds.push(id);
        }
      });
    }
    return customerEventIds;
  }

  isEventCustomer() {
    const customerTags = window?.BARREL?.customerTags || [];
    return customerTags.includes("registration:partner:guest-events");
  }

  fetchActiveEvents() {
    if (this.isEventCustomer()) {
      const isSessionToken = sessionStorage.getItem("isSessionToken");
      fetch(`${window.BARREL.sbdApiUrl}/events/active`, {
        method: "GET",
        headers: {
          token: isSessionToken,
          customerId: window.BARREL.customerId,
        },
      })
        .then((resp) => resp.json())
        .then((data) => {
          const { list } = data;
          this.activeEvents = list;
        })
        .catch((err) => console.log("Error while fetchActiveEvents", err));
    }
  }

  customerHasActiveEvents() {
    const customerEventIds = this.getCustomerEventIds();
    // console.log({ activeEvents: this.activeEvents, customerEventIds });
    if (this.activeEvents?.length === 0 || customerEventIds?.length === 0) {
      return false;
    } else if (this.activeEvents?.length && customerEventIds?.length) {
      return customerEventIds.some((e) =>
        this.activeEvents.map((a) => a.id).includes(e)
      );
    } else {
      return true;
    }
  }

  handleCheckout() {
    if (!this.isEventCustomer() || this.customerHasActiveEvents()) {
      this.checkout();
    } else {
      this.clearCartItems().then(() => {
        window.location = "/account";
      });
    }
  }

  openDrawer(event) {
    if (event && event.preventDefault) {
      if (event.metaKey) {
        return;
      }
      event.preventDefault();

      const buttonEl = event.target.closest("a");
      if (buttonEl && buttonEl.classList.contains("js-cart-icon")) {
        buttonEl.setAttribute("aria-expanded", "true");
      }
    }
    this.classList.add("open");
    this.fetchActiveEvents();

    this.checkoutBtn = this.querySelector("#CartDrawer-Checkout");
    if (this.checkoutBtn) {
      this.checkoutBtn.addEventListener(
        "click",
        this.handleCheckout.bind(this)
      );
    }

    if (document.body.classList.contains("user-is-tabbing")) {
      trapFocus(this, {
        elementToFocus: this.querySelector(".js-close-mini-cart"),
      });
    }

    if (!window.location.pathname.includes("/cart")) {
      document.body.classList.add("overflow-hidden");
    }

    viewCartDataLayer(true);
  }

  closeDrawer() {
    this.classList.remove("open");
    document
      .querySelector(".js-cart-icon")
      .setAttribute("aria-expanded", "false");
    if (!window.location.pathname.includes("/cart")) {
      document.body.classList.remove("overflow-hidden");
    }

    if (document.body.classList.contains("user-is-tabbing")) {
      trapFocus(document.body, {
        elementToFocus: document.querySelector(".js-cart-icon"),
      });
    }
  }

  // Add item to cart using HTML form data

  async addItemFromForm(form) {
    const formData = new FormData(form);
    const variantId = formData.get("id");
    const addQty = parseInt(formData.get("quantity")) || 1;

    // Dynamic max allowed = 6 × addQty
    const maxAllowed = 6 * addQty;

    const cart = await fetch("/cart.js").then((res) => res.json());

    const existingItem = cart.items.find(
      (item) => item.variant_id == variantId
    );
    const currentQty = existingItem ? existingItem.quantity : 0;

    if (currentQty >= maxAllowed) {
      alert(`You cannot add more than ${maxAllowed} of this item.`);
      return;
    }

    const newQty = Math.min(maxAllowed, currentQty + addQty);

    this.setAttribute("data-loading", true);

    if (existingItem) {
      return fetch("/cart/change.js", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          id: existingItem.key,
          quantity: newQty,
          sections: ["site-header", "cart"],
          sections_url: window.location.pathname,
        }),
      }).then(this.onCartUpdate);
    }

    formData.set("quantity", Math.min(maxAllowed, addQty));
    formData.append("sections", "site-header,cart");
    formData.append("sections_url", window.location.pathname);

    return fetch("/cart/add.js", {
      method: "POST",
      body: formData,
    }).then(this.onCartUpdate);
  }

  // Add item to cart

  async addItem(data) {
    const variantId = data.items[0].id;
    const addQty = parseInt(data.items[0].quantity) || 1;

    const maxAllowed = 6 * addQty;

    const cart = await fetch("/cart.js").then((res) => res.json());

    const existingItem = cart.items.find(
      (item) => item.variant_id == variantId
    );
    const currentQty = existingItem ? existingItem.quantity : 0;

    if (currentQty >= maxAllowed) {
      alert(`You cannot add more than ${maxAllowed} of this item.`);
      return;
    }

    const newQty = Math.min(maxAllowed, currentQty + addQty);

    this.setAttribute("data-loading", true);

    if (existingItem) {
      return fetch("/cart/change.js", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          id: existingItem.key,
          quantity: newQty,
          sections: ["site-header", "cart"],
          sections_url: window.location.pathname,
        }),
      }).then(this.onCartUpdate);
    }

    return fetch("/cart/add.js", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        items: [
          {
            id: variantId,
            quantity: Math.min(maxAllowed, addQty),
          },
        ],
        sections: "site-header,cart",
        sections_url: window.location.pathname,
      }),
    }).then(this.onCartUpdate);
  }

  // Sets all cart attributes
  updateAttributes(attributes) {
    return fetch("/cart/update.js", {
      credentials: "same-origin",
      method: "POST",
      headers: {
        "X-Requested-With": "XMLHttpRequest",
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ attributes }),
    }).then((response) => response.json());
  }

  // Update items in cart using HTML form data
  updateFromForm(form) {
    const formData = new FormData(form);
    formData.append("sections", "site-header,cart");
    formData.append("sections_url", window.location.pathname);

    this.setAttribute("data-loading", true);

    fetch("/cart/update.js", {
      method: "POST",
      body: formData,
    }).then(this.onCartUpdate);
  }

  changeItem(data) {
    this.setAttribute("data-loading", true);
    return fetch("/cart/change.js", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        ...data,
        sections: "site-header,cart",
        sections_url: window.location.pathname,
      }),
    }).then(this.onCartUpdate);
  }

  // Remove an item from cart using line item key
  removeItem(key) {
    this.setAttribute("data-loading", true);

    return fetch("/cart/update.js", {
      method: "POST",
      headers: {
        "content-type": "application/json",
      },
      body: JSON.stringify({
        updates: { [key]: 0 },
        sections: "site-header,cart",
        sections_url: window.location.pathname,
      }),
    }).then(this.onCartUpdate);
  }

  clearCartItems() {
    this.setAttribute("data-loading", true);
    return fetch("/cart/clear.js", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        sections: "site-header,cart",
        sections_url: window.location.pathname,
      }),
    }).then((res) => {
      // debugger;
      this.setAttribute("data-cart-empty", true);
      this.onCartUpdate(res);
    });
  }

  checkout(event) {
    const cartSubmitInput = document.createElement("input");
    cartSubmitInput.setAttribute("name", "checkout");
    cartSubmitInput.setAttribute("type", "hidden");
    this.querySelector("#cart_form").appendChild(cartSubmitInput);
    this.querySelector("#cart_form").submit();
  }

  // Handle cart API response and update sections with dynamic content
  onCartUpdate(cartResponse) {
    return cartResponse.json().then(({ sections }) => {
      // Replace content for re-rendered cart section
      const newCartEl = new DOMParser().parseFromString(
        sections.cart,
        "text/html"
      );
      this.replaceContent(newCartEl);

      // Replace content for re-rendered site-header section
      const newSiteHeaderEl = new DOMParser().parseFromString(
        sections["site-header"],
        "text/html"
      );
      document.querySelector("site-header").replaceContent(newSiteHeaderEl);

      this.setAttribute("data-loading", false);
      this.openDrawer();
    });
  }
}

customElements.define("shopify-cart", Cart);
